// ── API Endpoints (SANS préfixe /api — le client axios l'ajoute) ──
// Centralise les endpoints du module rh. Les anciens API_ENDPOINTS locaux
// (collaborateurs|contrats|feuilles-de-temps/config/constants.ts) sont supprimés.

export const API_ENDPOINTS = {
  DASHBOARD: "/rh/dashboard/metrics",

  // ── Ressources partagées (hors module rh) ──
  PERSONNES: {
    LIST: "/personnes",
    DETAIL: (id: number | string) => `/personnes/${id}` as const,
    INVITE: (id: number | string) => `/personnes/${id}/invite` as const,
    METRICS: "/rh/collaborateurs/metrics",
  },

  COMPETENCES: {
    LIST: "/competences",
    DETAIL: (id: number | string) => `/competences/${id}` as const,
    METRICS: "/rh/competences/metrics",
  },

  ABSENCES: {
    METRICS: "/rh/absences/metrics",
    TOP_ABSENTEES: "/rh/absences/top-absentees",
    UNREPORTED: "/rh/absences/unreported",
  },

  DEMANDE_ABSENCES: {
    LIST: "/demande_absences",
    DETAIL: (id: number | string) => `/demande_absences/${id}` as const,
    TRANSITION: (id: number | string) => `/demande_absences/${id}/transition` as const,
    VALIDATE: (id: number | string) => `/demande_absences/${id}/validate` as const,
    VALIDATION_STATE: (id: number | string) => `/demande_absences/${id}/validation-state` as const,
  },

  CONTRATS: {
    LIST: "/contrats",
    DETAIL: (id: number | string) => `/contrats/${id}` as const,
    METRICS: "/rh/contrats/metrics",
    HOURS_SUMMARY: (id: number | string) => `/contrats/${id}/hours_summary` as const,
    CANCEL_SIGNATURE: (id: number | string) => `/contrats/${id}/cancel-signature` as const,
    DOWNLOAD_SIGNED: (id: number | string) => `/contrats/${id}/download-signed` as const,
    PREVIEW_PDF: (id: number | string) => `/contrats/${id}/preview-pdf` as const,
    PREVIEW_PDF_SELF: (id: number | string) => `/contrats/${id}/preview-pdf-self` as const,
    INITIATE_SIGNATURE: (id: number | string) => `/contrats/${id}/initiate-signature` as const,
    RESEND_SIGNATURE: (id: number | string) => `/contrats/${id}/resend-signature` as const,
    SIGN_EMPLOYER: (id: number | string) => `/contrats/${id}/sign-employer` as const,
    SIGN_EMPLOYEE: (id: number | string) => `/contrats/${id}/sign-employee` as const,
    VALIDATE: (id: number | string) => `/contrats/${id}/validate` as const,
    PUBLIC_SIGNATURE: (token: string) => `/public/signatures/${token}` as const,
    AVENANTS: (id: number | string) => `/contrats/${id}/avenants` as const,
  },

  AVENANTS: {
    LIST: "/avenants",
    DETAIL: (id: number | string) => `/avenants/${id}` as const,
    CREATE: "/avenants",
    UPDATE: (id: number | string) => `/avenants/${id}` as const,
    VALIDATE: (id: number | string) => `/avenants/${id}/validate` as const,
    INITIATE_SIGNATURE: (id: number | string) => `/avenants/${id}/initiate-signature` as const,
    RESEND_SIGNATURE: (id: number | string) => `/avenants/${id}/resend-signature` as const,
    SIGN_EMPLOYER: (id: number | string) => `/avenants/${id}/sign-employer` as const,
    SIGN_EMPLOYEE: (id: number | string) => `/avenants/${id}/sign-employee` as const,
    CANCEL_SIGNATURE: (id: number | string) => `/avenants/${id}/cancel-signature` as const,
    DOWNLOAD_SIGNED: (id: number | string) => `/avenants/${id}/download-signed` as const,
    DOWNLOAD_SIGNED_SELF: (id: number | string) => `/avenants/${id}/download-signed-self` as const,
    PREVIEW_PDF: (id: number | string) => `/avenants/${id}/preview-pdf` as const,
    PREVIEW_PDF_SELF: (id: number | string) => `/avenants/${id}/preview-pdf-self` as const,
    BY_CONTRAT: (contratId: number | string) => `/contrats/${contratId}/avenants` as const,
    PENDING_ME: "/contrats/me/avenants-pending" as const,
    SIGNED_ME: "/contrats/me/avenants-signed" as const,
    PUBLIC_SIGNATURE: (token: string) => `/public/signatures-avenant/${token}` as const,
  },

  COMPTEURS_TEMPS: {
    DETAIL: (personneId: number | string) => `/rh/compteurs_temps/${personneId}` as const,
  },

  ETAT_CONTRATS: {
    LIST: "/etat_contrats",
    BY_CODE: (code: string) => `/etat_contrats?code=${code}` as const,
  },

  DOCUMENTS: {
    UPLOAD: "/documents/upload",
    DOWNLOAD: (id: number | string) => `/documents/${id}/download` as const,
    LIST: (entityType: string) => `/${entityType}` as const,
    DETAIL: (entityType: string, id: number | string) => `/${entityType}/${id}` as const,
    BULK_DELETE: (entityType: string) => `/${entityType}/bulk_delete` as const,
    BULK_MOVE: (entityType: string) => `/${entityType}/bulk_move` as const,
    BULK_DOWNLOAD: (entityType: string) => `/${entityType}/bulk_download` as const,
    BULK_STATUS_CHANGE: (entityType: string) => `/${entityType}/bulk_status_change` as const,
  },

  FEUILLES_DE_TEMPS: {
    // NB: l'entité API Platform est `feuille_de_temps` (snake_case),
    // le controller custom metrics utilise `feuilles-de-temps` (tirets).
    LIST: "/feuille_de_temps",
    DETAIL: (id: number | string) => `/feuille_de_temps/${id}` as const,
    METRICS: "/rh/feuilles-de-temps/metrics",
    WEEK_SUMMARY: "/rh/feuilles_de_temps/week_summary",
    BULK_VALIDATE: "/feuille_de_temps/bulk-validate",
    VALIDATE: (id: number | string) => `/feuille_de_temps/${id}/validate` as const,
    REJECT: (id: number | string) => `/feuille_de_temps/${id}/reject` as const,
  },

  PLANNING_ITEMS: {
    LIST: "/planning_items",
    DETAIL: (id: number | string) => `/planning_items/${id}` as const,
  },

  PLANNING_OCCURRENCES: {
    LIST: "/planning_occurrences",
  },

  NOTE_FRAIS: {
    LIST: "/note_frais",
    DETAIL: (id: number | string) => `/note_frais/${id}` as const,
  },

  POINTAGES: {
    LIST: "/pointages",
  },

  EXTRACTIONS: {
    EXPORT: "/rh/extractions/export",
  },

  INDICATEURS: {
    METRICS: "/rh/indicateurs/metrics",
  },
} as const;

// ──────────────────────────── Routes ────────────────────────────

export const ROUTES = {
  RH: {
    COLLABORATEURS: {
      LIST: "/rh/collaborateurs",
      CREATE: "/rh/collaborateurs/create",
      VIEW: "/rh/collaborateurs/$id/view",
      EDIT: "/rh/collaborateurs/$id/edit",
    },
    COMPETENCES: {
      LIST: "/rh/competences",
      CREATE: "/rh/competences/create",
      VIEW: "/rh/competences/$id/view",
      EDIT: "/rh/competences/$id/edit",
    },
    CONGES: {
      LIST: "/rh/conges",
      CREATE: "/rh/conges/create",
      VIEW: "/rh/conges/$id/view",
      ME: "/conges/me",
      NEW_REQUEST: "/conges/nouvelle-demande",
      DEMANDES_NEW: "/rh/conges/demandes/new",
    },
    CONTRATS: {
      LIST: "/rh/contrats",
      CREATE: "/rh/contrats/create",
      VIEW: "/rh/contrats/$id/view",
      EDIT: "/rh/contrats/$id/edit",
      ME: "/contrats/me",
    },
    NOTES_DE_FRAIS: {
      ME: "/frais/me",
      CREATE: "/rh/notes-de-frais/create",
      VIEW: "/rh/notes-de-frais/$id/view",
      // Routes employé "mes frais" (racine /frais/* — distinctes des routes
      // de gestion /rh/notes-de-frais/* ci-dessus).
      MES_VIEW: "/frais/$id/view",
      MES_EDIT: "/frais/$id/edit",
    },
    DOCUMENTS: {
      LIST: "/rh/documents",
      ME: "/documents/me",
    },
    PLANNING: {
      LIST: "/rh/planning",
      ME: "/planning/me",
    },
    POINTAGE: {
      ME: "/pointage/me",
    },
  },
} as const;
